import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface AppointmentReminder {
  id: string;
  session_id: string;
  reminder_type: string;
  sent_at: string;
  delivery_method: string;
  status: string;
}

export function useAppointmentReminders() {
  const { user } = useAuth();

  const { data: reminders, refetch } = useQuery({
    queryKey: ["appointment-reminders", user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Get reminders for the user's sessions
      const { data: sessions } = await supabase
        .from("telehealth_sessions")
        .select("id")
        .eq("member_id", user.id);

      if (!sessions || sessions.length === 0) return [];

      const sessionIds = sessions.map(s => s.id);

      const { data, error } = await supabase
        .from("appointment_reminders")
        .select("*")
        .in("session_id", sessionIds)
        .order("sent_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data as AppointmentReminder[];
    },
    enabled: !!user,
  });

  // Subscribe to new reminders in real-time
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("appointment-reminders-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "appointment_reminders",
        },
        async (payload) => {
          // Check if this reminder is for the current user's session
          const { data: session } = await supabase
            .from("telehealth_sessions")
            .select("member_id, scheduled_date, scheduled_time, visit_type")
            .eq("id", payload.new.session_id)
            .single();

          if (session && session.member_id === user.id) {
            const reminderType = payload.new.reminder_type;
            let message = "";

            switch (reminderType) {
              case "24_hour":
                message = "You have an appointment tomorrow!";
                break;
              case "1_hour":
                message = "Your appointment starts in 1 hour!";
                break;
              case "15_min":
                message = "Your appointment starts in 15 minutes! Join the waiting room now.";
                break;
            }

            toast.info("Appointment Reminder", {
              description: message,
              duration: 10000,
            });

            refetch();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, refetch]);

  return { reminders, refetch };
}
